import { FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Box } from "@mui/material";

import Player from "@pages/Catalog/components/Player/Player";
import { CatalogPageContext } from "@pages/Catalog/context";

import { useAppSelector } from "@store/hooks";
import { getAccumulatedTracks, getCatalogueData, getTracksData } from "@store/selectors/getCatalogueSelector";
import { getQuickFiltersSelector } from "@store/selectors/quickFilters";

import { ERoutes } from "@utils/constants";
import styled from "styled-components";

import AlbumList from "../../AlbumList";
import CatalogueSkeleton from "../../CatalogueSkeleton";
import { QuickFilterCategory } from "../../QuickFilterCategory";
import { CatalogTab } from "../../utils";

/* Load More — gold-outlined pill from the mobile design demo (accumulate).
   The ::before is a 1px divider inset to the track-row content width
   (cover → download button = 12px of SongStack padding each side). The button
   has equal space above (to the divider) and below (margin + the panel's
   bottom padding). */
const LoadMoreContainer = styled.div<{ $loading?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
  cursor: ${({ $loading }) => ($loading ? "default" : "pointer")};

  &::before {
    content: "";
    width: calc(100% - 24px);
    height: 1px;
    background: rgba(235, 194, 142, 0.15);
  }

  &:hover button:not(:disabled) {
    background: rgba(186, 144, 92, 0.1);
    box-shadow: 0 0 14px rgba(186, 144, 92, 0.2);
  }
  &:active button:not(:disabled) {
    transform: scale(0.98);
  }
`;

const LoadMoreButton = styled.button<{ $loading?: boolean }>`
  margin: 22px 0 10px;
  padding: 13px 40px;
  border-radius: 24px;
  border: 1px solid #ba905c;
  background: transparent;
  color: #ffdca8;
  font-family: "Raleway", sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: inherit;
  pointer-events: none;
  transition: all 0.2s ease;
  position: relative;

  ${({ $loading }) =>
    $loading &&
    `
    color: rgba(255, 220, 168, 0.5);
    &::after {
      content: "";
      position: absolute;
      right: 14px;
      top: 50%;
      margin-top: -6px;
      width: 12px;
      height: 12px;
      border: 1.5px solid rgba(255, 220, 168, 0.3);
      border-top-color: #ffdca8;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  `}

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

type TMainContentMobile = {
  isCatalogueLoading: boolean;
  page: number;
  handleChange: (_event, value: number) => void;
  handleButtonPageChange: (e: boolean) => void;
} & PropsWithChildren;

export const CatalogTabMobile: FC<TMainContentMobile> = ({
  isCatalogueLoading,
  page,
  handleButtonPageChange,
}) => {
  const { state, isSmartSearch, smartSearchHasMore, handleSmartLoadMore } = useContext(CatalogPageContext);
  const { data: catalogueData, pageCount } = useAppSelector(getCatalogueData);
  const { pageCount: tracksPageCount } = useAppSelector(getTracksData);
  const accumulatedTracks = useAppSelector(getAccumulatedTracks);
  const { quickFiltersCategories: quickCategories, isLoading } = useAppSelector(getQuickFiltersSelector);

  const location = useLocation();

  // Coordinated reveal of the mobile albums list — mirrors CatalogTabDesktop.
  // Preload every cover URL, fade the whole list in together when ready so
  // cards don't pop in one at a time as their images stream.
  const [albumCoversReady, setAlbumCoversReady] = useState(false);
  useEffect(() => {
    setAlbumCoversReady(false);
    if (!catalogueData?.length) return;
    // Preload the SAME variant AlbumList renders (mediumCoverUrl) so the reveal
    // gate coordinates with what actually paints — otherwise the grid reveals on
    // the small covers and the displayed medium covers load cold afterwards.
    const urls = Array.from(
      new Set(
        catalogueData
          .map((a: any) => a.cardCoverUrl || a.mediumCoverUrl || a.largeCoverUrl || a.smallCoverUrl)
          .filter((u: any): u is string => Boolean(u)),
      ),
    );
    if (!urls.length) {
      setAlbumCoversReady(true);
      return;
    }
    let cancelled = false;
    Promise.all(
      urls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          }),
      ),
    ).then(() => {
      if (cancelled) return;
      // Double-rAF — paints opacity:0 before flipping to 1 so the transition
      // fires even when images are already cached (navigation-back case).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setAlbumCoversReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [catalogueData]);

  // Coordinated reveal for the mobile TRACKS tab — same pattern.
  const [trackCoversReady, setTrackCoversReady] = useState(false);
  useEffect(() => {
    setTrackCoversReady(false);
    if (!accumulatedTracks?.length) return;
    const urls = Array.from(
      new Set(
        accumulatedTracks
          // TrackCover row thumbnail renders smallCoverUrl — preload that.
          .map((t: any) => t.smallCoverUrl || t.largeCoverUrl)
          .filter((u: any): u is string => Boolean(u)),
      ),
    );
    if (!urls.length) {
      setTrackCoversReady(true);
      return;
    }
    let cancelled = false;
    Promise.all(
      urls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          }),
      ),
    ).then(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setTrackCoversReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [accumulatedTracks]);

  const getPageCount = () => {
    if (state.catalogTab === CatalogTab.ALBUM) {
      return pageCount;
    }
    if (state.catalogTab === CatalogTab.TRACK || location.pathname.includes(ERoutes.QuickFilter)) {
      return tracksPageCount;
    }
    return quickCategories.pageCount;
  };

  const hasMore = page < getPageCount();
  // Only show the loading skeleton on the FIRST load (no content yet). During a
  // load-more the list already has content — inserting a skeleton at the top
  // would shift everything down and make the screen jump.
  const hasExistingContent =
    state.catalogTab === CatalogTab.ALBUM ? catalogueData.length > 0 : accumulatedTracks.length > 0;

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      gap={"16px"}
      rowGap={
        state.catalogTab === CatalogTab.QUICK_FILTERS
          ? location.pathname.includes(ERoutes.QuickFilter)
            ? "16px"
            : "48px"
          : state.catalogTab === CatalogTab.TRACK
          ? "6px"
          : undefined
      }
      height="fit-content"
    >
      {(isLoading || isCatalogueLoading) && !hasExistingContent && <CatalogueSkeleton isCollapsed={false} />}

      {state.catalogTab === CatalogTab.ALBUM && (
        <Box
          gridColumn="span 12"
          sx={{
            opacity: albumCoversReady ? 1 : 0,
            transition: "opacity 700ms ease",
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
            gap: "12px",
          }}
        >
          {catalogueData.map((item) => (
            <AlbumList key={item._id} album={item} isCollapsed={state.isFiltersOpen} />
          ))}
        </Box>
      )}

      {(state.catalogTab === CatalogTab.TRACK || location.pathname.includes(ERoutes.QuickFilter)) && (
        <Box
          gridColumn="span 12"
          sx={{
            opacity: trackCoversReady ? 1 : 0,
            transition: "opacity 700ms ease",
            display: "contents",
          }}
        >
          {accumulatedTracks.map((item, idx) => (
            <Box key={item._id} gridColumn="span 12">
              <Player song={item} resultIndex={idx} />
            </Box>
          ))}
        </Box>
      )}

      {state.catalogTab === CatalogTab.QUICK_FILTERS &&
        !location.pathname.includes(ERoutes.QuickFilter) &&
        quickCategories.data
          .filter((item) => item.quickFilters.length)
          .map((item) => <QuickFilterCategory {...item} />)}

      {/* LOAD MORE — smart search */}
      {isSmartSearch &&
        smartSearchHasMore &&
        state.catalogTab === CatalogTab.TRACK &&
        !isCatalogueLoading &&
        accumulatedTracks.length > 0 && (
          <Box gridColumn="span 12">
            <LoadMoreContainer onClick={handleSmartLoadMore}>
              <LoadMoreButton type="button">Load More Tracks</LoadMoreButton>
            </LoadMoreContainer>
          </Box>
        )}

      {/* LOAD MORE — standard browse */}
      {!isSmartSearch &&
        (state.catalogTab !== CatalogTab.QUICK_FILTERS || location.pathname.includes(ERoutes.QuickFilter)) &&
        getPageCount() > 1 && (
          <Box gridColumn="span 12">
            <LoadMoreContainer
              $loading={isCatalogueLoading}
              onClick={() => !isCatalogueLoading && hasMore && handleButtonPageChange(true)}
            >
              <LoadMoreButton
                type="button"
                disabled={!hasMore}
                $loading={isCatalogueLoading}
                data-loading={isCatalogueLoading || undefined}
              >
                {state.catalogTab === CatalogTab.ALBUM ? "Load More Albums" : "Load More Tracks"}
              </LoadMoreButton>
            </LoadMoreContainer>
          </Box>
        )}
    </Box>
  );
};
